import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, orderBy, query, setDoc, getDoc } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Trash2, ExternalLink, PenTool, Save, Newspaper, RefreshCw, Loader2, Calendar, Sparkles, Zap } from 'lucide-react';
import { triggerAutoUpdate } from '../../utils/sitemapService';
import { callAI } from '../../utils/aiService';

const RSS_FEEDS = [
    { name: 'PhilStar Business', url: 'https://www.philstar.com/rss/business' },
    { name: 'Inquirer Business', url: 'https://business.inquirer.net/feed' }
];

const BPO_KEYWORDS = [
    'offshore staffing', 'outsourcing', 'remote work', 'work-from-home', 'wfh', 'remote talent',
    'virtual assistant', 'va', 'bpo', 'business process outsourcing', 'global talent',
    'talent acquisition', 'offshoring', 'cost savings', 'staffing trends', 'remote staffing',
    'it-bpm', 'back office', 'shared services'
];

const TARGET_COUNTRIES = [
    'us', 'usa', 'united states', 'america',
    'australia', 'au', 'aus',
    'canada', 'ca', 'canadian',
    'new zealand', 'nz',
    'singapore', 'sg',
    'philippines', 'ph', 'manila', 'philippine'
];

const NewsManager = () => {
    const navigate = useNavigate();

    const truncateAtWord = (text, limit) => {
        if (!text || text.length <= limit) return text;
        const sub = text.substring(0, limit);
        const lastSpace = sub.lastIndexOf(' ');
        if (lastSpace > 0) return sub.substring(0, lastSpace) + '...';
        return sub + '...';
    };
    const [newsItems, setNewsItems] = useState([]);
    const [viewMode, setViewMode] = useState('library'); // 'library' or 'reports'
    const [loading, setLoading] = useState(true);
    const [harvesting, setHarvesting] = useState(false);
    const [lastHarvest, setLastHarvest] = useState(null);
    const [newItem, setNewItem] = useState({
        title: '',
        url: '',
        summary: '',
        source: ''
    });
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [harvestStatus, setHarvestStatus] = useState({ current: 0, total: 0, label: '' });

    // Drafting Wizard State
    const [wizardItem, setWizardItem] = useState(null);
    const [draftConfig, setDraftConfig] = useState({
        type: 'Educational',
        tone: 'Leadership',
        useAI: false
    });

    const [aiCreds, setAiCreds] = useState(null);
    const [aiResearching, setAiResearching] = useState(false);
    const [aiProgress, setAiProgress] = useState(0);

    const hasInitialized = useRef(false);



    const fetchNews = useCallback(async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'relevant_news'), orderBy('date_added', 'desc'));
            const querySnapshot = await getDocs(q);
            const items = [];
            querySnapshot.forEach((doc) => {
                items.push({ id: doc.id, ...doc.data() });
            });
            setNewsItems(items);
        } catch (error) {
            console.error("Error fetching news:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    const autoHarvest = useCallback(async () => {
        try {
            const metaRef = doc(db, 'metadata', 'news_harvester');
            const metaSnap = await getDoc(metaRef);

            const today = new Date().toISOString().split('T')[0];
            const lastRun = metaSnap.exists() ? metaSnap.data().last_run : null;
            setLastHarvest(lastRun);

            if (lastRun === today) {
                console.log("Already harvested today.");
                return;
            }

            setHarvesting(true);
            setHarvestStatus({ current: 0, total: RSS_FEEDS.length, label: 'Initializing harvester...' });

            // Fetch existing URLs locally to avoid dependency loop with newsItems state
            const qExisting = query(collection(db, 'relevant_news'));
            const existingSnap = await getDocs(qExisting);
            const existingUrls = new Set(existingSnap.docs.map(doc => doc.data().url));

            let newItemsCount = 0;

            for (let i = 0; i < RSS_FEEDS.length; i++) {
                const feed = RSS_FEEDS[i];
                setHarvestStatus({
                    current: i + 1,
                    total: RSS_FEEDS.length,
                    label: `Syncing ${feed.name}...`
                });

                try {
                    const response = await fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(feed.url)}`);
                    const xmlText = await response.text();
                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(xmlText, "text/xml");
                    const items = xmlDoc.querySelectorAll("item");

                    for (const item of items) {
                        const title = item.querySelector("title")?.textContent || "";
                        let link = item.querySelector("link")?.textContent ||
                            item.querySelector("guid")?.textContent || "";
                        link = link.trim().replace(/^https?:\/\//i, 'https://').replace(/\s+/g, '');

                        if (link && !link.startsWith('http')) {
                            const baseUrl = new URL(feed.url).origin;
                            link = new URL(link, baseUrl).href;
                        }

                        const description = item.querySelector("description")?.textContent || "";
                        const pubDate = item.querySelector("pubDate")?.textContent || "";

                        const matchesWholeWord = (text, word) => {
                            const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                            const regex = new RegExp(`\\b${escapedWord}\\b`, 'i');
                            return regex.test(text);
                        };

                        const isRelevantTopic = BPO_KEYWORDS.some(kw =>
                            matchesWholeWord(title, kw) || matchesWholeWord(description, kw)
                        );

                        const isTargetCountry = TARGET_COUNTRIES.some(country =>
                            matchesWholeWord(title, country) || matchesWholeWord(description, country)
                        );

                        // Image Extraction Logic
                        let imageUrl = "";
                        const mediaContent = item.getElementsByTagName("media:content")[0];
                        if (mediaContent) {
                            imageUrl = mediaContent.getAttribute("url");
                        } else {
                            const enclosure = item.querySelector("enclosure[type^='image']");
                            if (enclosure) {
                                imageUrl = enclosure.getAttribute("url");
                            } else {
                                // Try to find img tag in description
                                const imgMatch = description.match(/<img[^>]+src="([^">]+)"/);
                                if (imgMatch) imageUrl = imgMatch[1];
                            }
                        }

                        if (isRelevantTopic && isTargetCountry && !existingUrls.has(link)) {
                            await addDoc(collection(db, 'relevant_news'), {
                                title,
                                url: link,
                                summary: truncateAtWord(description.replace(/<[^>]*>?/gm, ''), 250),
                                imageUrl, // Store extracted image URL
                                source: feed.name,
                                date_added: new Date().toISOString(),
                                published_date: pubDate,
                                topics: BPO_KEYWORDS.filter(kw => matchesWholeWord(title, kw) || matchesWholeWord(description, kw)),
                                countries: TARGET_COUNTRIES.filter(c => matchesWholeWord(title, c) || matchesWholeWord(description, c))
                            });
                            existingUrls.add(link);
                            newItemsCount++;
                        }
                    }
                } catch (feedError) {
                    console.error(`Error harvesting from ${feed.name}:`, feedError);
                }
            }

            await setDoc(metaRef, { last_run: today }, { merge: true });
            setLastHarvest(today);
            if (newItemsCount > 0) {
                fetchNews();
            }
        } catch (error) {
            console.error("Auto-harvest failed:", error);
        } finally {
            setHarvesting(false);
            setHarvestStatus({ current: 0, total: 0, label: '' });
        }
    }, [fetchNews]);

    useEffect(() => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        const init = async () => {
            await fetchNews();
            await autoHarvest();

            // Fetch AI credentials
            try {
                const docSnap = await getDoc(doc(db, 'admin_settings', 'ai_credentials'));
                if (docSnap.exists()) setAiCreds(docSnap.data());
            } catch (err) {
                console.error("Error fetching AI creds:", err);
            }
        };
        init();
    }, [fetchNews, autoHarvest]);

    const seedNews = async () => {
        if (!window.confirm("Add sample news items to the library?")) return;
        setLoading(true);
        setHarvestStatus({ current: 0, total: 1, label: 'Preparing sample data...' });
        try {
            const samples = [
                // Global & US Markets
                {
                    title: "BPO Outlook 2026: Why US Firms are Doubling Down on Offshore Operations",
                    source: "Reuters",
                    url: "https://www.reuters.com/business/offshore-outsourcing-growth-2026",
                    summary: "As labor costs in the US rise, Fortune 500 companies are shifting more complex back-office functions to specialized hubs in the Philippines and India to maintain margins.",
                    date_added: new Date().toISOString(),
                    topics: ['offshore staffing', 'outsourcing', 'cost savings'],
                    countries: ['us', 'ph']
                },
                {
                    title: "The SGE Impact: How Google's Search Generative Experience is Reshaping BPO Marketing",
                    source: "Search Engine Journal",
                    url: "https://www.searchenginejournal.com/sge-bpo-marketing-trends",
                    summary: "BPO firms are adapting to AI-driven search results by focusing on EEAT principles and proprietary data to maintain visibility in global talent acquisition markets.",
                    date_added: new Date().toISOString(),
                    topics: ['ai', 'talent acquisition', 'bpo'],
                    countries: ['us']
                },
                // AU & NZ Markets
                {
                    title: "Australian SMEs Pivot to 'Virtual Headquarters' in Manila to Combat Inflation",
                    source: "AU Business Review",
                    url: "https://www.business.com.au/remote-hq-manila-2026",
                    summary: "High overheads in Sydney and Melbourne are driving a new wave of AU businesses to establish dedicated remote teams for customer support and financial services.",
                    date_added: new Date().toISOString(),
                    topics: ['remote work', 'virtual assistant', 'cost savings'],
                    countries: ['au', 'ph']
                },
                {
                    title: "New Zealand Financial Firms Expand Remote Operations in Cebu Tech Parks",
                    source: "NZ Herald",
                    url: "https://www.nzherald.co.nz/business/nz-finance-remote-cebu",
                    summary: "New Zealand-based insurance and banking groups are finding success in scaling their technical support divisions within the growing Cebu BPO ecosystem.",
                    date_added: new Date().toISOString(),
                    topics: ['remote work', 'offshoring', 'shared services'],
                    countries: ['nz', 'ph']
                },
                // AI & Innovation
                {
                    title: "AI-Augmented BPO: Not Deleting Jobs, But Redefining Them",
                    source: "Forbes",
                    url: "https://www.forbes.com/ai-augmented-bpo-evolution",
                    summary: "The rise of LLMs and AI assistants is enabling BPO agents in the Philippines to handle 40% more complex tickets while improving customer satisfaction scores.",
                    date_added: new Date().toISOString(),
                    topics: ['ai', 'bpo', 'it-bpm'],
                    countries: ['ph', 'us']
                },
                {
                    title: "Cybersecurity Standards Tighten for Remote BPO Staff After 2025 Data Shield Act",
                    source: "TechCrunch",
                    url: "https://techcrunch.com/bpo-cybersecurity-trends-2026",
                    summary: "Global regulators are requiring stricter endpoint security for work-from-home agents, driving investment in zero-trust architectures for offshore hubs.",
                    date_added: new Date().toISOString(),
                    topics: ['remote work', 'cybersecurity', 'offshore staffing'],
                    countries: ['us', 'ca']
                },
                // Specialized VAs
                {
                    title: "Executive Virtual Assistants: The New Status Symbol for Silicon Valley Founders",
                    source: "The Verge",
                    url: "https://www.theverge.com/eva-silicon-valley-founders-trend",
                    summary: "Highly skilled Philippine VAs are now managing entire workflows, coding, and decision-making for startup founders, moving beyond simple scheduling tasks.",
                    date_added: new Date().toISOString(),
                    topics: ['virtual assistant', 'remote talent', 'global talent'],
                    countries: ['us', 'ph']
                },
                {
                    title: "Canadian Healthcare Taps Philippine BPO for Medical Transcription 2.0",
                    source: "Health Tech AU",
                    url: "https://www.healthtech.ca/medical-transcription-bpo-innovation",
                    summary: "Faced with healthcare worker shortages, Canadian provinces are partnering with BPO providers for AI-assisted medical coding and patient records management.",
                    date_added: new Date().toISOString(),
                    topics: ['specialized bpo', 'healthcare outsourcing', 'remote work'],
                    countries: ['ca', 'ph']
                },
                // Economic Trends
                {
                    title: "Manila BPO Real Estate Demand Hits 5-Year High Despite WFH Popularity",
                    source: "PropertyGuru",
                    url: "https://www.propertyguru.ph/manila-bpo-real-estate-boom-2026",
                    summary: "Hybrid work models are driving a need for specialized 'collaboration hubs' in BGC and Makati, balancing remote flexibility with high-security office infrastructure.",
                    date_added: new Date().toISOString(),
                    topics: ['shared services', 'it-bpm', 'back office'],
                    countries: ['ph']
                },
                {
                    title: "Singapore Tech Giants Outsource Level 2 Engineering to Davao City",
                    source: "ST News",
                    url: "https://www.straitstimes.com/tech-outsourcing-davao-2026",
                    summary: "Singapore firms are moving higher-order technical support to Mindanao's growing tech parks to optimize R&D budgets while accessing skilled engineering talent.",
                    date_added: new Date().toISOString(),
                    topics: ['it-bpm', 'offshore staffing', 'specialized bpo'],
                    countries: ['sg', 'ph']
                },
                // Add 30 more varied items to reach the goal
                ...Array.from({ length: 30 }).map((_, i) => ({
                    title: [
                        "Scaling BPO Success: How to Manage Cross-Border Teams",
                        "The Future of Voice: Will AI Replace Call Centers in 2027?",
                        "Philippine Economic Zone Authority (PEZA) Unveils New Incentives for Green BPOs",
                        "Data Privacy in the age of Remote Staffing: What US Employers Need to Know",
                        "Specialized BPO vs General Outsourcing: Which is Right for your Startup?",
                        "The Rise of the 'Micro-BPO': Specialized Boutique Agencies in the Philippines",
                        "AU Labor Laws and Ph Outsource Teams: Navigating Compliance",
                        "UK Firms follow US Lead in Manila Talent Acquisition",
                        "High-End creative BPO services booming in Makati",
                        "Mental Health in the BPO Industry: New Corporate Standards for 2026"
                    ][i % 10] + ` (Vol. ${Math.floor(i / 10) + 2})`,
                    source: ["BusinessWorld", "Manila Bulletin", "BBC Business", "WSJ", "Tech in Asia"][i % 5],
                    url: `https://placeholder-news-site.com/article-${i + 10}`,
                    summary: "An in-depth look at how the global outsourcing landscape is shifting toward more specialized, technical, and AI-augmented roles in established Southeast Asian hubs.",
                    date_added: new Date(Date.now() - (i * 3600000)).toISOString(),
                    topics: ['bpo', 'outsourcing', 'remote staffing', 'it-bpm'],
                    countries: ['ph', 'us', 'au', 'ca', 'sg', 'nz'][i % 6]
                }))
            ].map(item => ({
                ...item,
                countries: Array.isArray(item.countries) ? item.countries : [item.countries]
            }));

            setHarvestStatus({ current: 0, total: samples.length, label: 'Injecting sample library...' });

            for (let i = 0; i < samples.length; i++) {
                if (i % 5 === 0) {
                    setHarvestStatus({
                        current: i + 1,
                        total: samples.length,
                        label: `Batch seeding ${i + 1} of ${samples.length}...`
                    });
                }
                await addDoc(collection(db, 'relevant_news'), samples[i]);
            }
            fetchNews();
            alert("Sample news items added to library!");
        } catch (error) {
            console.error("Error seeding news:", error);
        } finally {
            setLoading(false);
            setHarvestStatus({ current: 0, total: 0, label: '' });
        }
    };

    const handleAddNews = async (e) => {
        e.preventDefault();
        if (!newItem.title || !newItem.url) return;

        try {
            await addDoc(collection(db, 'relevant_news'), {
                ...newItem,
                date_added: new Date().toISOString()
            });
            setNewItem({ title: '', url: '', summary: '', source: '' });
            fetchNews();
            // Trigger Sitemap Update
            await triggerAutoUpdate();
        } catch (error) {
            console.error("Error adding news:", error);
            alert("Failed to add news item.");
        }
    };

    const handleDelete = async (id) => {
        if (!id) {
            console.error("Delete failed: No ID provided");
            alert("Internal Error: No ID found for this item.");
            return;
        }
        if (!window.confirm("Are you sure you want to delete this item?")) return;
        try {
            await deleteDoc(doc(db, 'relevant_news', id));
            setNewsItems(newsItems.filter(item => item.id !== id));
            const newSelected = new Set(selectedIds);
            newSelected.delete(id);
            setSelectedIds(newSelected);
        } catch (error) {
            console.error("Error deleting news:", error);
            alert(`Failed to delete: ${error.message}. Please ensure you are logged in.`);
        }
    };

    const handleBatchDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} selected items?`)) return;

        setLoading(true);
        try {
            let successCount = 0;
            const idsToDelete = Array.from(selectedIds);

            for (const id of idsToDelete) {
                try {
                    await deleteDoc(doc(db, 'relevant_news', id));
                    successCount++;
                } catch (e) {
                    console.error(`Failed to delete item ${id}:`, e);
                }
            }

            setNewsItems(newsItems.filter(item => !selectedIds.has(item.id)));
            setSelectedIds(new Set());

            if (successCount < idsToDelete.length) {
                alert(`Partially completed: Deleted ${successCount} of ${idsToDelete.length} items. Check console for details.`);
            }
        } catch (error) {
            console.error("Error in batch deletion:", error);
            alert(`Batch deletion failed: ${error.message}`);
        } finally {
            setLoading(false);
            fetchNews(); // Refresh list to sync with DB
        }
    };

    const toggleSelection = (id) => {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedIds(newSelected);
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === filteredItems.length && filteredItems.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredItems.map(item => item.id)));
        }
    };

    const [selectedReportDate, setSelectedReportDate] = useState(null);

    const handleGenerateArticle = (item) => {
        const contentArea = document.querySelector('.content');
        if (contentArea) {
            contentArea.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setWizardItem(item);
    };

    const handleBatchGenerateArticle = (items) => {
        const contentArea = document.querySelector('.content');
        if (contentArea) {
            contentArea.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        // Create a synthetic item for the wizard
        const syntheticItem = {
            id: 'batch-' + Date.now(),
            title: items.length > 2 ? `Executive Briefing: ${items[0].title} and ${items.length - 1} related shifts` : items.map(i => i.title).join(' & '),
            summary: items.map(i => i.summary).join('\n\n'),
            url: items[0].url, // use primary
            batchItems: items
        };
        setWizardItem(syntheticItem);
    };

    const confirmDrafting = async () => {
        if (!wizardItem) return;

        // Check if AI is integrated if user wants to use it
        if (draftConfig.useAI) {
            const hasAI = aiCreds?.openai_key || aiCreds?.gemini_key || aiCreds?.perplexity_key || aiCreds?.grok_key;
            if (!hasAI) {
                if (!window.confirm("No AI systems (OpenAI, Gemini, Perplexity, or Grok) are currently integrated. Would you like to proceed with standard drafting without AI help?")) {
                    return;
                }
                setDraftConfig(prev => ({ ...prev, useAI: false }));
            }
        }

        let aiResearch = null;
        if (draftConfig.useAI && aiCreds) {
            setAiResearching(true);
            setAiProgress(10);
            try {
                // USE UNIFIED RESEARCH BRIEF FLOW (Exhaustive Fallback)
                setAiProgress(30);
                aiResearch = await callAI('research_brief', wizardItem.title, aiCreds);
                setAiProgress(80);
            } catch (err) {
                console.error("AI Research Error:", err);
                alert("AI enhancement failed. Proceeding with standard draft.");
            } finally {
                setAiProgress(100);
                setTimeout(() => setAiResearching(false), 500);
            }
        }

        const promptParams = {
            ...wizardItem,
            draftType: draftConfig.type,
            draftTone: draftConfig.tone,
            aiResearch: aiResearch,
            useAI: draftConfig.useAI,
            majorSource: wizardItem.url,
            provider: 'exhaustive_chain',
            instructions: `
                Act as an Expert BPO Consultant. 
                Follow EEAT (Expertise, Experience, Authoritativeness, Trust) principles.
                Target SEO and SGE (Search Generative Experience) with bolded key takeaways and bullet points.
                Article Type: ${draftConfig.type}
                Tone: ${draftConfig.tone}
                Major Source: ${wizardItem.url}
                Generate a COMPLETELY UNIQUE perspective based on latest data. Avoid generic content, motivational language, fluff, or sales tone.
                Every paragraph must contain data, operational insight, or applied business interpretation. If a paragraph lacks analytical value, omit it.
                ${aiResearch ? `Integrated AI Research: ${aiResearch}` : 'Include references to latest industry studies and data.'}
            `
        };

        navigate('/admin/blog/new', { state: { newsItem: promptParams } });
        setWizardItem(null);
    };

    const getReportDates = () => {
        const dates = [...new Set(newsItems.map(item => new Date(item.date_added).toDateString()))];
        return dates.sort((a, b) => new Date(b) - new Date(a));
    };

    const filteredItems = selectedReportDate
        ? newsItems.filter(item => new Date(item.date_added).toDateString() === selectedReportDate)
        : newsItems;

    return (
        <>
            <div className="panel active">
                <div className="page-hdr">
                    <div className="page-hdr-left">
                        <div className="eyebrow">Content</div>
                        <h1>Relevant News Manager <span>.</span></h1>
                        <div className="flex items-center gap-4 mt-2">
                            {lastHarvest && (
                                <div className="flex items-center text-[10px] text-gray-400">
                                    <Calendar size={10} className="mr-1" />
                                    Last Harvest: {lastHarvest}
                                </div>
                            )}
                            <div className="h-3 w-[1px] bg-gray-200"></div>
                            <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200">
                                <button
                                    onClick={() => { setViewMode('library'); setSelectedReportDate(null); }}
                                    className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${viewMode === 'library' ? 'bg-white text-[#A2CB52] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    Active Library
                                </button>
                                <button
                                    onClick={() => setViewMode('reports')}
                                    className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${viewMode === 'reports' ? 'bg-white text-[#A2CB52] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    Report Library
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="page-hdr-actions">
                        <button
                            className="btn btn-ghost"
                            onClick={seedNews}
                            disabled={harvesting || loading}
                        >
                            <RefreshCw size={14} className={harvesting ? "animate-spin" : ""} />
                            {harvesting ? "Harvesting..." : "Seed Data"}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Add New Item Form */}
                    <div className="lg:col-span-1">
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm sticky top-6">
                            <h2 className="text-lg font-bold text-[#20222D] mb-4 flex items-center">
                                <Plus size={20} className="mr-2 text-[#A2CB52]" />
                                Add News Item
                            </h2>
                            <form onSubmit={handleAddNews} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Article Title</label>
                                    <input
                                        type="text"
                                        value={newItem.title}
                                        onChange={e => setNewItem({ ...newItem, title: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#A2CB52] focus:border-[#A2CB52]"
                                        placeholder="e.g. Philippines BPO Industry Grows by 10%"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Source Name</label>
                                    <input
                                        type="text"
                                        value={newItem.source}
                                        onChange={e => setNewItem({ ...newItem, source: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#A2CB52] focus:border-[#A2CB52]"
                                        placeholder="e.g. Bloomberg, TechCrunch"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                                    <input
                                        type="url"
                                        value={newItem.url}
                                        onChange={e => setNewItem({ ...newItem, url: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#A2CB52] focus:border-[#A2CB52]"
                                        placeholder="https://..."
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Summary / Key Takeaways</label>
                                    <textarea
                                        value={newItem.summary}
                                        onChange={e => setNewItem({ ...newItem, summary: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#A2CB52] focus:border-[#A2CB52]"
                                        rows="4"
                                        placeholder="Briefly summarize why this is relevant..."
                                    ></textarea>
                                </div>
                                <button
                                    type="submit"
                                    className="w-full bg-[#20222D] text-white font-bold py-3 rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center"
                                >
                                    <Save size={18} className="mr-2" />
                                    Save to Library
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* News List */}
                    <div className="lg:col-span-2 space-y-6">
                        {(harvesting || (loading && harvestStatus.total > 0)) && (
                            <div className="bg-[#ECF5DC] p-6 rounded-xl border border-[#A2CB52]/20 shadow-sm overflow-hidden animate-in fade-in slide-in-from-top-4 duration-300">
                                <div className="flex justify-between items-center mb-4">
                                    <div className="flex items-center text-[#20222D] font-bold">
                                        <Loader2 className="animate-spin mr-3 text-[#A2CB52]" size={20} />
                                        {harvestStatus.label || 'Processing Industry Intelligence...'}
                                    </div>
                                    <span className="text-xs font-bold text-[#A2CB52] bg-white px-2 py-1 rounded-lg border border-[#ECF5DC]">
                                        {Math.round((harvestStatus.current / harvestStatus.total) * 100) || 0}% Complete
                                    </span>
                                </div>

                                {/* Progress Bar Container */}
                                <div className="w-full h-2 bg-white rounded-full overflow-hidden border border-gray-100 p-0.5">
                                    <div
                                        className="h-full bg-gradient-to-r from-[#A2CB52] to-[#8fb840] rounded-full transition-all duration-500 ease-out"
                                        style={{ width: `${(harvestStatus.current / harvestStatus.total) * 100}%` }}
                                    ></div>
                                </div>

                                <div className="flex justify-between mt-2">
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                                        Step {harvestStatus.current} of {harvestStatus.total}
                                    </span>
                                    <span className="text-[10px] text-gray-400 italic">
                                        Finalizing regional market data...
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                            <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50/50">
                                <h2 className="text-lg font-bold text-[#20222D] flex items-center">
                                    {viewMode === 'library' ? (
                                        <><Newspaper size={20} className="mr-2 text-[#A2CB52]" /> Harvested News Library</>
                                    ) : (
                                        <><Save size={20} className="mr-2 text-[#A2CB52]" /> Report Archive</>
                                    )}
                                </h2>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-medium text-gray-500 bg-white px-2 py-1 rounded-full border border-gray-100">
                                        {viewMode === 'library' ? `${newsItems.length} items total` : `${getReportDates().length} daily reports`}
                                    </span>
                                    {selectedIds.size > 0 && (
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => {
                                                    const items = newsItems.filter(i => selectedIds.has(i.id));
                                                    handleBatchGenerateArticle(items);
                                                }}
                                                className="flex items-center gap-1.5 px-3 py-1 bg-[#ECF5DC] text-[#A2CB52] text-[10px] font-bold rounded-lg border border-[#A2CB52]/20 hover:bg-[#A2CB52]/10 transition-colors"
                                            >
                                                <PenTool size={12} />
                                                Draft {selectedIds.size}
                                            </button>
                                            <button
                                                onClick={handleBatchDelete}
                                                className="flex items-center gap-1.5 px-3 py-1 bg-red-50 text-red-600 text-[10px] font-bold rounded-lg border border-red-100 hover:bg-red-100 transition-colors"
                                            >
                                                <Trash2 size={12} />
                                                Delete {selectedIds.size}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {loading && !harvesting ? (
                                <div className="p-12 text-center">
                                    <Loader2 className="animate-spin mx-auto text-[#A2CB52] mb-4" size={32} />
                                    <p className="text-gray-500 font-medium tracking-tight">Loading archives...</p>
                                </div>
                            ) : viewMode === 'reports' && !selectedReportDate ? (
                                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {getReportDates().map(dateStr => (
                                        <button
                                            key={dateStr}
                                            onClick={() => setSelectedReportDate(dateStr)}
                                            className="flex items-center justify-between p-4 bg-gray-50 border border-gray-100 rounded-xl hover:border-[#A2CB52] hover:bg-white transition-all text-left shadow-sm group"
                                        >
                                            <div>
                                                <div className="text-xs font-bold text-[#20222D] mb-1">{new Date(dateStr).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</div>
                                                <div className="text-[10px] text-gray-400 flex items-center">
                                                    <Newspaper size={10} className="mr-1" />
                                                    {newsItems.filter(i => new Date(i.date_added).toDateString() === dateStr).length} harvested items
                                                </div>
                                            </div>
                                            <ExternalLink size={14} className="text-gray-300 group-hover:text-[#A2CB52]" />
                                        </button>
                                    ))}
                                    {getReportDates().length === 0 && (
                                        <div className="col-span-2 py-12 text-center text-gray-400">No reports available.</div>
                                    )}
                                </div>
                            ) : newsItems.length === 0 ? (
                                <div className="p-12 text-center text-gray-400">
                                    <p>No news items added yet. Click seed data or wait for auto-harvest.</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {selectedReportDate && (
                                        <div className="bg-[#ECF5DC]/30 px-6 py-3 flex justify-between items-center border-b border-[#A2CB52]/10">
                                            <div className="flex items-center gap-4">
                                                <div className="text-xs font-bold text-[#20222D]">
                                                    Report for: <span className="text-[#A2CB52]">{selectedReportDate}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.size === filteredItems.length && filteredItems.length > 0}
                                                        onChange={toggleSelectAll}
                                                        className="w-3.5 h-3.5 rounded border-gray-300 text-[#A2CB52] focus:ring-[#A2CB52]"
                                                    />
                                                    <span className="text-[10px] font-bold text-gray-500 uppercase">Select All</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setSelectedReportDate(null)}
                                                className="text-[10px] font-bold text-gray-400 hover:text-gray-600 uppercase tracking-tighter"
                                            >
                                                Back to archives
                                            </button>
                                        </div>
                                    )}
                                    {!selectedReportDate && newsItems.length > 0 && (
                                        <div className="bg-gray-50/50 px-6 py-2 border-b border-gray-100 flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.size === newsItems.length && newsItems.length > 0}
                                                onChange={toggleSelectAll}
                                                className="w-3.5 h-3.5 rounded border-gray-300 text-[#A2CB52] focus:ring-[#A2CB52]"
                                            />
                                            <span className="text-[10px] font-bold text-gray-500 uppercase">Select All Library</span>
                                        </div>
                                    )}
                                    {filteredItems.map((item, index) => {
                                        const showDateHeader = !selectedReportDate && (index === 0 ||
                                            new Date(item.date_added).toDateString() !== new Date(newsItems[index - 1].date_added).toDateString());

                                        return (
                                            <React.Fragment key={item.id}>
                                                {showDateHeader && (
                                                    <div className="bg-gray-50/80 px-6 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest border-y border-gray-100 flex items-center">
                                                        <Calendar size={10} className="mr-2" />
                                                        {new Date(item.date_added).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                                    </div>
                                                )}
                                                <div className={`p-6 hover:bg-gray-50 transition-colors group relative border-l-4 border-transparent hover:border-l-[#A2CB52] ${selectedIds.has(item.id) ? 'bg-[#ECF5DC]/20 border-l-[#A2CB52]' : ''}`}>
                                                    <div className="flex justify-between items-start gap-4">
                                                        <div className="pt-1 select-none">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedIds.has(item.id)}
                                                                onChange={() => toggleSelection(item.id)}
                                                                className="w-4 h-4 rounded border-gray-300 text-[#A2CB52] focus:ring-[#A2CB52] cursor-pointer"
                                                            />
                                                        </div>
                                                        <div className="flex-grow pr-4">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="text-[10px] font-bold text-[#A2CB52] uppercase tracking-wider bg-[#ECF5DC] px-2 py-0.5 rounded">
                                                                    {item.source || 'Authority Source'}
                                                                </span>
                                                                <span className="text-[10px] text-gray-400">
                                                                    {item.published_date || new Date(item.date_added).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                            </div>
                                                            <h3 className="text-lg font-bold text-[#20222D] mb-2 group-hover:text-[#A2CB52] transition-colors">{item.title}</h3>
                                                            <p className="text-sm text-gray-600 mb-3 line-clamp-3 leading-relaxed">{item.summary}</p>

                                                            {item.topics && (
                                                                <div className="flex flex-wrap gap-1 mb-3">
                                                                    {item.topics.slice(0, 3).map(t => (
                                                                        <span key={t} className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded italic">#{t.replace(/\s+/g, '')}</span>
                                                                    ))}
                                                                    {item.countries && item.countries.map(c => (
                                                                        <span key={c} className="text-[9px] font-bold text-blue-400 bg-blue-50 px-1.5 py-0.5 rounded uppercase">{c}</span>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            <div className="flex items-center gap-4">
                                                                <a
                                                                    href={item.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center text-xs font-bold text-blue-600 hover:text-blue-800"
                                                                >
                                                                    Source Article <ExternalLink size={10} className="ml-1" />
                                                                </a>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col gap-2 shrink-0">
                                                            <button
                                                                onClick={(e) => handleGenerateArticle(item, e)}
                                                                className="bg-[#20222D] text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-[#A2CB52] transition-colors flex items-center justify-center whitespace-nowrap shadow-sm"
                                                                title="Generate SEO Blog Post"
                                                            >
                                                                <PenTool size={14} className="mr-2" />
                                                                Draft Article
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(item.id)}
                                                                className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center"
                                                                title="Delete from Library"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Drafting Wizard Modal (Anchored) */}
            {wizardItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm overflow-y-auto">
                    <div
                        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl h-fit overflow-hidden animate-in fade-in slide-in-from-top-4 duration-300 relative"
                        style={{ margin: 'auto' }}
                    >
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-[#20222D] text-white">
                            <div>
                                <h2 className="text-xl font-bold flex items-center gap-2">
                                    <PenTool size={20} className="text-[#A2CB52]" />
                                    Smart Drafting Wizard
                                </h2>
                                <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest font-bold">Step 1: Configure Strategy</p>
                            </div>
                            <button
                                onClick={() => setWizardItem(null)}
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                <Plus size={24} className="rotate-45" />
                            </button>
                        </div>

                        <div className="p-8 space-y-6">
                            {/* Type Selection */}
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Article Type</label>
                                <div className="grid grid-cols-2 gap-3">
                                    {['Educational', 'Industry Analysis', 'Opinion/Feedback', 'Case Study'].map(type => (
                                        <button
                                            key={type}
                                            onClick={() => setDraftConfig({ ...draftConfig, type })}
                                            className={`px-4 py-3 rounded-xl border-2 text-sm font-bold transition-all ${draftConfig.type === type
                                                ? 'border-[#A2CB52] bg-[#ECF5DC] text-[#20222D]'
                                                : 'border-gray-100 bg-gray-50 text-gray-400 hover:border-gray-200'
                                                }`}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Tone Selection */}
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Tone of Voice</label>
                                <div className="grid grid-cols-2 gap-3">
                                    {['Leadership', 'Educator', 'Conversational', 'Professional'].map(tone => (
                                        <button
                                            key={tone}
                                            onClick={() => setDraftConfig({ ...draftConfig, tone })}
                                            className={`px-4 py-3 rounded-xl border-2 text-sm font-bold transition-all ${draftConfig.tone === tone
                                                ? 'border-[#A2CB52] bg-[#ECF5DC] text-[#20222D]'
                                                : 'border-gray-100 bg-gray-50 text-gray-400 hover:border-gray-200'
                                                }`}
                                        >
                                            {tone}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* AI Intelligence Enhancement */}
                            <div className="pt-4 border-t border-gray-100">
                                <div
                                    onClick={() => setDraftConfig({ ...draftConfig, useAI: !draftConfig.useAI })}
                                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center justify-between ${draftConfig.useAI
                                        ? 'border-[#A2CB52] bg-[#ECF5DC]'
                                        : 'border-gray-100 bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${draftConfig.useAI ? 'bg-[#A2CB52] text-white' : 'bg-gray-200 text-gray-400'}`}>
                                            <Sparkles size={16} />
                                        </div>
                                        <div>
                                            <h4 className={`text-sm font-bold ${draftConfig.useAI ? 'text-[#20222D]' : 'text-gray-400'}`}>AI Intelligence Enhancement</h4>
                                            <p className="text-[10px] text-gray-500">Enable 1500+ word expert drafting & image gen</p>
                                        </div>
                                    </div>
                                    <div className={`w-10 h-6 rounded-full relative transition-all ${draftConfig.useAI ? 'bg-[#A2CB52]' : 'bg-gray-300'}`}>
                                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${draftConfig.useAI ? 'left-5' : 'left-1'}`}></div>
                                    </div>
                                </div>

                                {draftConfig.useAI && (
                                    <div className="bg-[#090e18] p-4 rounded-xl border border-gray-200 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Connected Resources</span>
                                            <Link to="/admin/ai-settings" className="text-[10px] text-[#A2CB52] hover:underline">Manage APIs</Link>
                                        </div>
                                        <div className="flex gap-2">
                                            {aiCreds?.openai_key && <div className="w-5 h-5 rounded bg-green-500/10 flex items-center justify-center text-[8px] font-bold text-green-500 border border-green-500/20" title="ChatGPT Active">G</div>}
                                            {aiCreds?.gemini_key && <div className="w-5 h-5 rounded bg-blue-500/10 flex items-center justify-center text-[8px] font-bold text-blue-500 border border-blue-500/20" title="Gemini Active">M</div>}
                                            {aiCreds?.perplexity_key && <div className="w-5 h-5 rounded bg-teal-500/10 flex items-center justify-center text-[8px] font-bold text-teal-500 border border-teal-500/20" title="Perplexity Active">P</div>}
                                            {!aiCreds?.openai_key && !aiCreds?.gemini_key && !aiCreds?.perplexity_key && <span className="text-[10px] text-gray-500 italic">No APIs connected. Using defaults.</span>}
                                        </div>
                                        <p className="text-[10px] text-gray-500 leading-relaxed italic">
                                            "System will fetch updated 2026 data points and industry insights before drafting."
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-6 bg-gray-50 flex flex-col gap-4">
                            {aiResearching && (
                                <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2">
                                    <div className="flex justify-between items-center">
                                        <div className="text-[10px] font-bold text-[#A2CB52] uppercase flex items-center gap-2">
                                            <Loader2 size={12} className="animate-spin" />
                                            Processing...
                                        </div>
                                        <span className="text-[10px] font-bold text-gray-400">{aiProgress}%</span>
                                    </div>
                                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-[#A2CB52] transition-all duration-500"
                                            style={{ width: `${aiProgress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setWizardItem(null)}
                                    className="flex-1 py-3 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDrafting}
                                    disabled={aiResearching}
                                    className="flex-[2] bg-[#20222D] text-white py-3 rounded-xl text-sm font-bold hover:bg-[#A2CB52] transition-colors shadow-lg shadow-gray-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {aiResearching ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin text-[#A2CB52]" />
                                            Analyzing Data...
                                        </>
                                    ) : (
                                        <>
                                            <Zap size={18} className={draftConfig.useAI ? "text-[#A2CB52]" : ""} />
                                            {draftConfig.useAI ? 'Enhance & Draft' : 'Start Standard Draft'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default NewsManager;
